import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { copyText } from "../../lib/clipboard";
import { Copy, ExternalLink, Globe, Loader2, Rocket } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import DomainSection from "./DomainSection";
import { api, errMsg } from "../../lib/api";

const toSlug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export const PublishDialog = ({ open, onOpenChange, project, onChange }) => {
  const [slug, setSlug] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const live = !!project.published_version_id;
  const url = `${window.location.origin}/p/${project.slug || slug}`;
  const outdated = live && project.published_version_id !== project.current_version_id;

  useEffect(() => { if (open) setSlug(project.slug || toSlug(project.name)); }, [open, project.slug, project.name]);
  useEffect(() => {
    if (!open || slug.length < 3) return setCheck(null);
    const t = setTimeout(() => api.checkSlug(slug, project.id).then(setCheck).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [slug, open, project.id]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { onChange(await fn()); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] bg-white border-slate-200 max-h-[90vh] overflow-y-auto thin-scroll" data-testid="publish-dialog">
        <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2"><Rocket size={18} className="text-emerald-600" /> Publish your app</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">Host it on Ishukart with one click. Re-publish any time to push your latest version live.</DialogDescription>

        {live && (
          <div className="min-w-0 rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="publish-live-panel">
            <div className="flex items-center gap-2 text-sm text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live · version {project.published_version_number}</div>
            <div className="flex items-center gap-2">
              <a href={`/p/${project.slug}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate text-sm font-mono text-slate-900 hover:underline" data-testid="publish-live-url">{url}</a>
              <button onClick={() => copyText(url, "Link copied")} data-testid="publish-copy-url" className="text-slate-500 hover:text-slate-900" aria-label="Copy link"><Copy size={14} /></button>
              <a href={`/p/${project.slug}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900" aria-label="Open"><ExternalLink size={14} /></a>
            </div>
            {outdated && <p className="text-xs text-amber-600">You have newer changes that aren't live yet.</p>}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-500">Your app address</label>
          <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 focus-within:border-sky-300 overflow-hidden">
            <span className="pl-3 text-sm text-slate-500 font-mono whitespace-nowrap">{window.location.host}/p/</span>
            <input value={slug} onChange={(e) => setSlug(toSlug(e.target.value))} data-testid="publish-slug-input"
              className="flex-1 min-w-0 h-10 bg-transparent pr-3 text-sm font-mono text-slate-900 outline-none" />
          </div>
          {check && <p className={`text-xs ${check.available ? "text-emerald-600" : "text-rose-600"}`} data-testid="publish-slug-status">{check.available ? "Available" : check.reason}</p>}
        </div>

        <div className="flex gap-2">
          <button onClick={() => act(() => api.publish(project.id, slug), live ? "Live site updated" : "Your app is live!")} disabled={busy || !project.current_version_id || (check && !check.available)}
            data-testid="publish-confirm-btn" className="flex-1 h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />} {live ? "Update live site" : "Publish"}
          </button>
          {live && (
            <button onClick={() => act(() => api.unpublish(project.id), "App unpublished")} disabled={busy} data-testid="publish-unpublish-btn"
              className="h-11 px-4 rounded-lg border border-slate-300 text-sm text-slate-600 hover:text-rose-600 hover:border-rose-300">Unpublish</button>
          )}
        </div>

        <div className="pt-4 border-t border-slate-200 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Globe size={15} className="text-indigo-600" /> Custom domain</h4>
          <DomainSection project={project} onChange={onChange} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublishDialog;
